import { useEffect, useState } from "react";
import { useModal } from "react-modal-hook";

export const useLocalStorage = (key, initialValue) => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.error(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.error(error);
    }
  };

  return [storedValue, setValue];
};

export default function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Set debouncedValue to value (passed in) after the specified delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Return a cleanup function that will be called every time ...
      // ... useEffect is re-called. useEffect will only be re-called ...
      // ... if value changes (see the inputs array below).
      // This is how we prevent debouncedValue from changing if value is ...
      // ... changed within the delay period. Timeout gets cleared and restarted.
      // To put it in context, if the user is typing within our app's ...
      // ... search box, we don't want the debouncedValue to update until ...
      // ... they've stopped typing for more than 500ms.
      return () => {
        clearTimeout(handler);
      };
    },
    // Only re-call effect if value changes
    // You could also add the "delay" var to inputs array if you ...
    // ... need to be able to change that dynamically.
    [value, delay]
  );

  return debouncedValue;
}

//when calling
//example: const modal = useDialogHook(CampaignModal);
//call to show: modal(props, onCallBackFunction)
export const useDialogHook = (Component) => {
  const [myProps, setMyProps] = useState({});

  const [show, hide] = useModal(() => {
    return (
      <Component
        hide={(args) => {
          hide();
          if (myProps.onCloseCallback) myProps.onCloseCallback(args);
        }}
        {...myProps}
      />
    );
  }, [myProps]);

  return (props, onCloseCallback) => {
    let tprops = props || {};
    if (onCloseCallback) tprops.onCloseCallback = onCloseCallback;

    setMyProps(tprops);
    show();
  };
};

// get windows w and h
const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
};

export const useWindowDimensions = () => {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );
  const [isMobile, setIsMobile] = useState(
    getWindowDimensions().width < 768
  );

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
      setIsMobile(getWindowDimensions().width < 768);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return { width: windowDimensions.width, height: windowDimensions.height, isMobile };
};
//end: get windows w and h

//Year
// export const useYear = () => {
//   let earliestYear = 1970;
//   const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
//   const [year, setYear] = useState([]);

//   // while (currentYear >= earliestYear) {
//   //   //setYear([...year, { label: currentYear, value: currentYear }]);
//   //   //setCurrentYear((prev) => prev - 1);
//   // }

//   return year;
// };
export const uuidv4 = () => {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16)
  );
};
